import React, { useState } from "react";

function Banner({ bg, borderColor, icon, title, subtext, primaryLabel, onPrimary, secondaryLabel, onSecondary, dismissible, onDismiss }) {
  return (
    <div
      style={{
        background: bg,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: "12px",
        padding: "16px 20px",
        marginBottom: "20px",
        display: "flex",
        alignItems: "flex-start",
        gap: "14px",
      }}
    >
      <span style={{ fontSize: "1.3rem", flexShrink: 0, marginTop: "2px", lineHeight: 1 }}>
        {icon}
      </span>

      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--text, #1C1C1E)" }}>
          {title}
        </div>
        {subtext && (
          <div style={{ fontSize: "0.85rem", color: "var(--text-soft, #6b7280)", marginTop: "3px", lineHeight: 1.5 }}>
            {subtext}
          </div>
        )}
        {(primaryLabel || secondaryLabel) && (
          <div style={{ marginTop: "10px", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
            {primaryLabel && (
              <button
                type="button"
                onClick={onPrimary}
                style={{
                  background: "var(--primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {primaryLabel}
              </button>
            )}
            {secondaryLabel && (
              <button
                type="button"
                onClick={onSecondary}
                style={{
                  background: "none",
                  border: "1px solid var(--border, #e5e7eb)",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  color: "var(--text, #1C1C1E)",
                  whiteSpace: "nowrap",
                }}
              >
                {secondaryLabel}
              </button>
            )}
          </div>
        )}
      </div>

      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: "none",
            border: "none",
            color: "var(--text-faint, #9ca3af)",
            fontSize: "1.1rem",
            cursor: "pointer",
            flexShrink: 0,
            alignSelf: "flex-start",
            lineHeight: 1,
            padding: "0",
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

export default function SubscriptionBanner({ subscription, onShowPricing }) {
  const [dismissed, setDismissed] = useState(false);

  if (!subscription || subscription.loading) return null;

  const {
    isTrialing,
    isFree,
    isSoleTrader,
    trialDaysRemaining,
    isJobLimitReached,
    jobCompletions,
  } = subscription;

  if (dismissed) return null;

  const d = trialDaysRemaining;
  const dayLabel = d === 1 ? "day" : "days";

  // 1. Trial ending soon
  if (isTrialing && d <= 3) {
    return (
      <Banner
        // Use --dark-panel-bg so the banner lifts to the elevated dark surface
        // in dark mode (previously hardcoded #1C1C1E blended into the body).
        bg="var(--dark-panel-bg, #1C1C1E)"
        borderColor="var(--primary)"
        icon="⏰"
        title={<span style={{ color: "#fff" }}>Your free trial ends in {d} {dayLabel}.</span>}
        subtext={<span style={{ color: "rgba(255,255,255,0.65)" }}>After that you'll drop to the free plan — 3 jobs max. Upgrade to keep everything.</span>}
        primaryLabel="Upgrade now →"
        onPrimary={onShowPricing}
        dismissible
        onDismiss={() => setDismissed(true)}
      />
    );
  }

  // 2. Trial active (> 3 days remaining)
  if (isTrialing && d > 3) {
    return (
      <Banner
        bg="rgba(255, 138, 0,0.06)"
        borderColor="var(--primary)"
        icon="🎉"
        title={`You're on a free trial — ${d} days left.`}
        subtext="Full access to everything. No card needed yet."
        secondaryLabel="See plans →"
        onSecondary={onShowPricing}
        dismissible
        onDismiss={() => setDismissed(true)}
      />
    );
  }

  // 3. Job limit reached
  if (isJobLimitReached) {
    if (isFree) {
      return (
        <Banner
          bg="rgba(245,158,11,0.08)"
          borderColor="#F59E0B"
          icon="🔒"
          title="You've used your 5 free jobs this month."
          subtext="Upgrade to Pro for 20 jobs/mo + 200 free credits — less than $1 a day."
          primaryLabel="Upgrade — from $29/mo →"
          onPrimary={onShowPricing}
          dismissible={false}
        />
      );
    }
    if (isSoleTrader) {
      return (
        <Banner
          bg="rgba(245,158,11,0.08)"
          borderColor="#F59E0B"
          icon="🔒"
          title="You've reached your 20 job limit this month."
          subtext="Upgrade to Ultimate for unlimited jobs + 1,000 free credits/mo."
          primaryLabel="Upgrade to Ultimate →"
          onPrimary={onShowPricing}
          dismissible={false}
        />
      );
    }
  }

  // 4. Approaching limit
  if (isFree && jobCompletions === 4) {
    return (
      <Banner
        bg="rgba(245,158,11,0.06)"
        borderColor="#F59E0B"
        icon="⚠️"
        title="1 free job remaining this month."
        subtext="Upgrade to Pro for 20 jobs/mo and 200 free marketplace credits."
        secondaryLabel="See upgrade options →"
        onSecondary={onShowPricing}
        dismissible
        onDismiss={() => setDismissed(true)}
      />
    );
  }

  if (isSoleTrader && jobCompletions >= 18) {
    return (
      <Banner
        bg="rgba(245,158,11,0.06)"
        borderColor="#F59E0B"
        icon="⚠️"
        title={`${20 - jobCompletions} job${20 - jobCompletions === 1 ? "" : "s"} remaining this month.`}
        subtext="Upgrade to Ultimate for unlimited jobs."
        secondaryLabel="See upgrade options →"
        onSecondary={onShowPricing}
        dismissible
        onDismiss={() => setDismissed(true)}
      />
    );
  }

  // 5. Free with no completions — don't show anything
  return null;
}
